/**
 * McKee's review library.
 * Keeps direct Judge.me reviews separate from clearly labelled category reviews.
 * Lingue: italiano, tedesco, inglese.
 */
(function () {
  'use strict';

  const refreshKey = 'mckProductReviewsRefresh';

  if (window.mckProductReviewsAssetInitialized) {
    window[refreshKey]?.(document);
    return;
  }

  window.mckProductReviewsAssetInitialized = true;

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const taxonomyRules = [
    { pattern: /softshell|soft shell/, family: 'GIACCHE', subcategory: 'Giacche in Softshell' },
    { pattern: /antipioggia|impermeabil|rain jacket|regenjacke|wasserdicht/, family: 'GIACCHE', subcategory: 'Giacche impermeabili' },
    { pattern: /antivento|windbreaker|wind shell|guscio|windjacke|hardshell/, family: 'GIACCHE', subcategory: 'Giacche antivento' },
    { pattern: /imbottit|termic|piumin|daunen|wattiert|gepolstert/, family: 'GIACCHE', subcategory: 'Giacche imbottite' },
    { pattern: /giacca da sci|ski jacket|skijacke/, family: 'GIACCHE', subcategory: 'Giacche da sci' },
    { pattern: /giacca in pile|fleece jacket|fleecejacke/, family: 'GIACCHE', subcategory: 'Giacche in pile' },
    { pattern: /giacca|jacket|jacke/, family: 'GIACCHE', subcategory: '' },
    { pattern: /micropile|pile|mid layer|midlayer|fleece/, family: 'PILE / MID-LAYER', subcategory: 'Pile e mid-layer' },
    { pattern: /pantalonc|bermuda|shorts/, family: 'PANTALONCINI', subcategory: 'Pantaloncini da trekking' },
    { pattern: /pantalon|trouser|pants|hose/, family: 'PANTALONI', subcategory: 'Pantaloni da trekking' },
    { pattern: /gilet|vest|weste/, family: 'GILET', subcategory: 'Gilet' },
    { pattern: /t shirt|tshirt|canotta|tank top|top donna|top uomo/, family: 'T-SHIRT / TOP', subcategory: 'T-Shirt' },
    { pattern: /calzin|sock/, family: 'ACCESSORI', subcategory: 'Calzini' },
    { pattern: /bastonc|trekking pole|stocke/, family: 'ACCESSORI', subcategory: 'Bastoncini da trekking' },
    { pattern: /zaino|backpack|rucksack/, family: 'ACCESSORI', subcategory: 'Zaini' },
    { pattern: /berrett|cappell|hat|beanie|mutze/, family: 'ACCESSORI', subcategory: 'Berretti' },
    { pattern: /scaldacollo|neck warmer|guant|glove|accessori|handschuh/, family: 'ACCESSORI', subcategory: '' }
  ];

  const categoryLabels = {
    'giacche in softshell': { it: 'giacche softshell', en: 'softshell jackets', de: 'Softshelljacken' },
    'giacche impermeabili': { it: 'giacche impermeabili', en: 'waterproof jackets', de: 'wasserdichte Jacken' },
    'giacche antivento': { it: 'giacche antivento', en: 'windproof jackets', de: 'winddichte Jacken' },
    'giacche imbottite': { it: 'giacche imbottite', en: 'insulated jackets', de: 'wattierte Jacken' },
    'giacche da sci': { it: 'giacche da sci', en: 'ski jackets', de: 'Skijacken' },
    'giacche in pile': { it: 'giacche in pile', en: 'fleece jackets', de: 'Fleecejacken' },
    'pile e mid layer': { it: 'pile e mid-layer', en: 'fleece and mid-layers', de: 'Fleece und Midlayer' },
    'pantaloncini da trekking': { it: 'pantaloncini da trekking', en: 'trekking shorts', de: 'Trekkingshorts' },
    'pantaloni da trekking': { it: 'pantaloni da trekking', en: 'trekking trousers', de: 'Trekkinghosen' },
    't shirt': { it: 't-shirt tecniche', en: 'technical T-shirts', de: 'Funktions-T-Shirts' },
    'calzini': { it: 'calzini', en: 'socks', de: 'Socken' },
    'bastoncini da trekking': { it: 'bastoncini da trekking', en: 'trekking poles', de: 'Trekkingstöcke' },
    'zaini': { it: 'zaini', en: 'backpacks', de: 'Rucksäcke' },
    'berretti': { it: 'berretti', en: 'hats', de: 'Mützen' },
    'gilet': { it: 'gilet', en: 'vests', de: 'Westen' }
  };

  const TEXT = {
    it: {
      numLocale: 'it-IT',
      similar: 'prodotti simili',
      summary: (label, avg, n) => `Recensioni su ${label}: ${avg} su 5 · ${n} verificate`,
      verified: 'Acquisto verificato',
      source: 'Recensione per:',
      of: 'su',
      disclosure: (label) => `Queste recensioni riguardano altri modelli McKee's della categoria ${label}.`
    },
    de: {
      numLocale: 'de-DE',
      similar: 'ähnliche Produkte',
      summary: (label, avg, n) => `Bewertungen zu ${label}: ${avg} von 5 · ${n} verifiziert`,
      verified: 'Verifizierter Kauf',
      source: 'Bewertung für:',
      of: 'von',
      disclosure: (label) => `Diese Bewertungen beziehen sich auf andere McKee's Modelle der Kategorie ${label}.`
    },
    en: {
      numLocale: 'en-GB',
      similar: 'similar products',
      summary: (label, avg, n) => `Reviews of ${label}: ${avg} out of 5 · ${n} verified`,
      verified: 'Verified purchase',
      source: 'Review for:',
      of: 'out of',
      disclosure: (label) => `These reviews are for other McKee's products in the ${label} category.`
    }
  };

  const genderFrom = (value) => {
    const normalized = normalize(value);
    if (/\b(donna|women|woman|female|femmin|damen)/.test(normalized)) return 'women';
    if (/\b(uomo|men|man|male|masch|herren)/.test(normalized)) return 'men';
    if (/\b(unisex|uni sex)/.test(normalized)) return 'unisex';
    return '';
  };

  const taxonomyFor = (element) => {
    const library = Array.isArray(window.MckReviewLibrary) ? window.MckReviewLibrary : [];
    const familyCandidate = String(element.dataset.reviewFamily || '').trim();
    const subcategoryCandidate = String(element.dataset.reviewSubcategory || '').trim();
    const explicitFamily = library.some((review) => normalize(review.family) === normalize(familyCandidate))
      ? familyCandidate
      : '';
    const explicitSubcategory = library.some((review) => normalize(review.subcategory) === normalize(subcategoryCandidate))
      ? subcategoryCandidate
      : '';
    const haystack = normalize([
      element.dataset.productTitle,
      element.dataset.productType,
      element.dataset.productTags
    ].join(' '));
    const inferred = taxonomyRules.find((rule) => rule.pattern.test(haystack)) || { family: '', subcategory: '' };

    return {
      family: explicitFamily || inferred.family,
      subcategory: explicitSubcategory || inferred.subcategory,
      gender: genderFrom(`${element.dataset.reviewGender || ''} ${element.dataset.productTitle || ''}`),
      season: normalize(element.dataset.reviewSeason || '')
    };
  };

  const scoreReview = (review, taxonomy) => {
    const reviewGender = genderFrom(review.product);
    const genderScore = !taxonomy.gender || !reviewGender
      ? 1
      : reviewGender === taxonomy.gender
        ? 3
        : reviewGender === 'unisex'
          ? 2
          : 0;
    const substanceScore = Math.min(review.text.length, 180) / 180;
    return genderScore * 10 + substanceScore + (review.date ? 1 : 0);
  };

  const relatedFor = (element) => {
    const library = Array.isArray(window.MckReviewLibrary) ? window.MckReviewLibrary : [];
    const taxonomy = taxonomyFor(element);
    const currentHandle = element.dataset.productHandle || '';
    const familyKey = normalize(taxonomy.family);
    const subcategoryKey = normalize(taxonomy.subcategory);
    const eligible = library.filter((review) => review.handle !== currentHandle);
    const exact = subcategoryKey
      ? eligible.filter((review) => normalize(review.subcategory) === subcategoryKey)
      : [];
    const family = familyKey
      ? eligible.filter((review) => (
        normalize(review.family) === familyKey
        && normalize(review.subcategory) !== subcategoryKey
      ))
      : [];
    const sortByRelevance = (left, right) => (
      scoreReview(right, taxonomy) - scoreReview(left, taxonomy)
      || String(right.date || '').localeCompare(String(left.date || ''))
    );

    exact.sort(sortByRelevance);
    family.sort(sortByRelevance);

    return {
      taxonomy,
      exact,
      all: [...exact, ...family]
    };
  };

  const languageFor = (element) => {
    const l = String(element.dataset.locale || document.documentElement.lang || 'it').toLowerCase();
    if (l.startsWith('it')) return 'it';
    if (l.startsWith('de')) return 'de';
    return 'en';
  };

  const categoryLabel = (taxonomy, language) => {
    const key = normalize(taxonomy.subcategory || taxonomy.family);
    return categoryLabels[key]?.[language]
      || (language === 'it'
        ? String(taxonomy.subcategory || taxonomy.family || TEXT.it.similar).toLowerCase()
        : (TEXT[language] || TEXT.en).similar);
  };

  const summaryPool = (related) => related.exact.length > 0 ? related.exact : related.all;

  const renderSummary = (element) => {
    const related = relatedFor(element);
    const reviews = summaryPool(related);
    const wrapper = element.querySelector('[data-mck-review-similar-summary]');
    const copy = element.querySelector('[data-mck-review-similar-copy]');

    if (!wrapper || !copy || reviews.length === 0) return;

    const language = languageFor(element);
    const t = TEXT[language] || TEXT.en;
    const average = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
    const formattedAverage = average.toLocaleString(t.numLocale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
    const label = categoryLabel(related.taxonomy, language);

    copy.textContent = t.summary(label, formattedAverage, reviews.length);
    wrapper.hidden = false;
  };

  const safeReviewUrl = (value, language) => {
    try {
      const url = new URL(value, window.location.origin);
      if (!(url.protocol === 'https:' && /(^|\.)mckees\.it$/i.test(url.hostname))) return '';
      if (language === 'de' && !/^\/de(\/|$)/.test(url.pathname)) url.pathname = '/de' + url.pathname;
      return url.href;
    } catch (error) {
      return '';
    }
  };

  const renderCard = (review, language) => {
    const t = TEXT[language] || TEXT.en;
    const rating = Math.max(1, Math.min(5, Number.parseInt(review.rating, 10) || 0));
    const percentage = rating * 20;
    const sourceUrl = safeReviewUrl(review.url, language);
    const sourceProduct = escapeHtml(review.product);
    const source = sourceUrl
      ? `<a href="${escapeHtml(sourceUrl)}">${sourceProduct}</a>`
      : `<strong>${sourceProduct}</strong>`;

    return `
      <article class="mck-review-card">
        <div class="mck-review-stars" style="--mck-review-fill: ${percentage}%" role="img" aria-label="${rating} ${t.of} 5">
          <span aria-hidden="true">★★★★★</span>
        </div>
        <blockquote class="mck-review-card__quote">“${escapeHtml(review.text)}”</blockquote>
        <div class="mck-review-card__meta">
          <span class="mck-review-card__author">${escapeHtml(review.author)}</span>
          <span class="mck-review-card__verified">✓ ${t.verified}</span>
        </div>
        <div class="mck-review-card__source">
          <span class="mck-review-card__source-label">${t.source}</span>
          ${source}
        </div>
      </article>`;
  };

  const activateTab = (root, targetName, moveFocus = false) => {
    root.querySelectorAll('[data-mck-review-tab]').forEach((tab) => {
      const active = tab.dataset.mckReviewTab === targetName;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && moveFocus) tab.focus();
    });

    root.querySelectorAll('[data-mck-review-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.mckReviewPanel !== targetName;
    });

    if (targetName === 'direct') {
      window.setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    }
  };

  const renderSection = (root) => {
    if (root.dataset.mckReviewRendered === 'true') return;

    const related = relatedFor(root);
    const maxCards = Math.max(1, Number.parseInt(root.dataset.maxCards || '3', 10));
    const selectedReviews = related.all.slice(0, maxCards);
    const language = languageFor(root);
    const t = TEXT[language] || TEXT.en;
    const cards = root.querySelector('[data-mck-review-cards]');
    const empty = root.querySelector('[data-mck-review-related-empty]');
    const count = root.querySelector('[data-mck-review-similar-count]');
    const disclosure = root.querySelector('[data-mck-review-disclosure]');
    const visiblePool = summaryPool(related);

    if (count) count.textContent = String(selectedReviews.length);

    if (disclosure && visiblePool.length > 0) {
      disclosure.textContent = t.disclosure(categoryLabel(related.taxonomy, language));
    }

    if (cards) cards.innerHTML = selectedReviews.map((review) => renderCard(review, language)).join('');
    if (empty) empty.hidden = selectedReviews.length > 0;

    if (selectedReviews.length === 0 && Number.parseInt(root.dataset.directReviewCount || '0', 10) === 0) {
      activateTab(root, 'direct');
      const similarTab = root.querySelector('[data-mck-review-tab="similar"]');
      if (similarTab) similarTab.disabled = true;
    }

    root.dataset.mckReviewRendered = 'true';
  };

  const elementsWithin = (root, selector) => {
    const elements = [];
    if (root instanceof Element && root.matches(selector)) elements.push(root);
    if (root?.querySelectorAll) elements.push(...root.querySelectorAll(selector));
    return elements;
  };

  const refresh = (root = document) => {
    elementsWithin(root, '[data-mck-review-summary]').forEach(renderSummary);
    elementsWithin(root, '[data-mck-product-reviews]').forEach(renderSection);
  };

  window[refreshKey] = refresh;

  document.addEventListener('click', (event) => {
    const tab = event.target.closest?.('[data-mck-review-tab]');
    if (tab) {
      const root = tab.closest('[data-mck-product-reviews]');
      if (root) activateTab(root, tab.dataset.mckReviewTab);
      return;
    }

    const jump = event.target.closest?.('[data-mck-review-jump="similar"]');
    if (jump) {
      const root = document.querySelector('[data-mck-product-reviews]');
      if (root) activateTab(root, 'similar');
      return;
    }

    const writeButton = event.target.closest?.('[data-mck-write-review]');
    if (writeButton) {
      const root = writeButton.closest('[data-mck-product-reviews]');
      if (!root) return;
      activateTab(root, 'direct');
      const judgeMeButton = root.querySelector('.jdgm-write-rev-link, [data-write-review], .jdgm-write-rev-link.jdgm-write-rev-link');
      if (judgeMeButton) {
        judgeMeButton.click();
      } else {
        root.querySelector('[data-mck-judge-me-widget]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[data-mck-review-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    const root = tab.closest('[data-mck-product-reviews]');
    const tabs = Array.from(root.querySelectorAll('[data-mck-review-tab]:not(:disabled)'));
    const currentIndex = tabs.indexOf(tab);
    const offset = event.key === 'ArrowRight' ? 1 : -1;
    const nextTab = tabs[(currentIndex + offset + tabs.length) % tabs.length];

    event.preventDefault();
    activateTab(root, nextTab.dataset.mckReviewTab, true);
  });

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }
})();
